import React, { useEffect, useState } from "react";
import { Box, Button, Paper, Snackbar, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPut } from "../../lib";

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 640, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(3) },
  blurb: { color: theme.palette.text.secondary, marginBottom: theme.spacing(3) },
  row: { display: "flex", alignItems: "flex-end", gap: theme.spacing(2) },
  field: { width: 120 },
  input: {
    fontSize: 13,
    height: 30,
    padding: "2px 8px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: 3,
    width: "100%",
    boxSizing: "border-box",
  },
  label: { fontSize: 11, marginBottom: 2, display: "block" },
  hint: { color: theme.palette.text.secondary, marginTop: theme.spacing(2) },
  error: { color: theme.palette.error.main, marginTop: theme.spacing(1) },
}));

/**
 * How long a sold watch stays on the site before it disappears.
 *
 * While inside the window the storefront still shows the watch, but its status
 * reads Sold and the Add to Cart button is gone — the page keeps its ranking
 * and can still convert to an enquiry.
 */
export function PreownedSoldExpiresPage() {
  const classes = useStyles();
  const [days, setDays] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    (async () => {
      const { data, error: err } = await authGet("/preowned/settings");
      if (err) setError(err.message || "Could not load the setting.");
      else setDays(String(data?.sold_expires_days ?? 30));
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  async function save() {
    const n = Number(days);
    if (!Number.isFinite(n) || n < 0 || n > 3650) {
      setError("Enter a whole number of days between 0 and 3650.");
      return;
    }
    setSaving(true);
    setError("");
    const { error: err } = await authPut("/preowned/settings", {
      sold_expires_days: Math.floor(n),
    });
    setSaving(false);
    if (err) setError(err.message || "Save failed");
    else setNotice("Sold Expires At updated.");
  }

  return (
    <Box className={classes.page}>
      <Paper className={classes.panel}>
        <Typography variant="h6" gutterBottom>
          Sold Expires At
        </Typography>
        <Typography variant="body2" className={classes.blurb}>
          How many days a sold watch stays on the site after it is marked sold.
          While it is still showing, the status reads <b>Sold</b> and the Add to
          Cart button is removed. Once the window passes the watch drops off the
          site for good.
        </Typography>

        <div className={classes.row}>
          <div className={classes.field}>
            <label className={classes.label} htmlFor="sold-days">
              Days
            </label>
            <input
              id="sold-days"
              type="number"
              min="0"
              max="3650"
              className={classes.input}
              value={days}
              disabled={!loaded || saving}
              onChange={(e) => setDays(e.target.value)}
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={!loaded || saving}
          >
            Save
          </Button>
        </div>

        {error && (
          <Typography variant="body2" className={classes.error}>
            {error}
          </Typography>
        )}

        <Typography variant="body2" className={classes.hint}>
          Set to <b>0</b> to remove a watch as soon as it is marked sold.
        </Typography>
      </Paper>

      <Snackbar
        open={!!notice}
        autoHideDuration={3500}
        onClose={() => setNotice("")}
        message={notice}
      />
    </Box>
  );
}

export default PreownedSoldExpiresPage;
